<div class="indication-container">
  <!-- Header Section -->
  <div class="header-section">
    <div class="header-content">
      <div class="header-text">
        <h1 class="page-title">
          <ng-container *ngIf="referralHeadline; else defaultIndicationTitle">
            {{ referralHeadline }}
          </ng-container>
          <ng-template #defaultIndicationTitle>
            {{ 'pages.indication.title' | i18n }}
          </ng-template>
        </h1>

        <p class="page-subtitle">
          <ng-container *ngIf="referralSubheadline; else defaultIndicationSubtitle">
            {{ referralSubheadline }}
          </ng-container>
          <ng-template #defaultIndicationSubtitle>
            {{ 'pages.indication.subtitle' | i18n }}
          </ng-template>
        </p>

        <p class="page-call-to-action">{{ 'pages.indication.callToAction' | i18n }}</p>

        <ustore-button
          class="indicate-cta"
          [textColor]="'var(--color-inverted)'"
          [size]="'medium'"
          [backgroundColor]="'var(--color-red-500-to-yellow)'"
          [text]="referralButtonLabel || ('pages.indication.buttonIndicate' | i18n)"
          [rounded]="'default'"
          (onClick)="onClick($event)"
        ></ustore-button>

        <!-- Mobile Video below button -->
        <div *ngIf="showMobileMedia" class="mobile-video-section" [class.scrolled]="isScrolled">
          <img
            *ngIf="showMobileBannerImage; else mobileIndicationVideo"
            [src]="referralBannerMobileUrl"
            class="mobile-indication-video"
            [alt]="'pages.indication.videoTitle' | i18n"
            (error)="onMobileBannerError()"
          />
          <ng-template #mobileIndicationVideo>
            <video #headerVideoMobile class="mobile-indication-video" controls autoplay muted playsinline preload="auto" (click)="onMobileVideoClick()">
              <source src="https://mkt-image-front.s3.us-east-1.amazonaws.com/claro_programa_de_indica%C3%A7%C3%A3o.mp4" type="video/mp4" />
              Seu navegador não suporta o elemento de vídeo.
            </video>
            <div *ngIf="showSoundOverlay" class="video-overlay" (click)="onMobileOverlayClick($event)">
              {{ 'pages.indication.videoOverlay' | i18n }}
            </div>
          </ng-template>
          <div class="video-title">{{ 'pages.indication.videoTitle' | i18n }}</div>
          <button class="close-floating-video" (click)="closeFloatingVideo()">×</button>
        </div>
      </div>

      <div *ngIf="showDesktopMedia" class="header-image mobile-hidden">
        <img
          *ngIf="showDesktopBannerImage; else desktopIndicationVideo"
          [src]="referralBannerUrl"
          class="indication-image"
          [alt]="'pages.indication.videoTitle' | i18n"
          (error)="onDesktopBannerError()"
        />
        <ng-template #desktopIndicationVideo>
          <video #headerVideo class="indication-image" controls autoplay playsinline preload="auto">
            <source src="https://mkt-image-front.s3.us-east-1.amazonaws.com/claro_programa_de_indica%C3%A7%C3%A3o.mp4" type="video/mp4" />
            Seu navegador não suporta o elemento de vídeo.
          </video>
          <div *ngIf="showSoundOverlay" class="video-overlay" (click)="startVideoWithSound()">
            {{ 'pages.indication.videoOverlay' | i18n }}
          </div>
        </ng-template>
      </div>
    </div>
  </div>

  <!-- Main Form -->
  <div class="form-section">
    <div class="form-container">
      <form [formGroup]="indicationForm" (ngSubmit)="onSubmit($event)" class="indication-form" novalidate="false">
        <div class="campaign-rules-button">
          <ustore-button
            [textColor]="'var(--color-inverted)'"
            [size]="'large'"
            [backgroundColor]="'var(--color-red-800-to-yellow)'"
            [text]="'pages.indication.rulesButton' | i18n"
            [rounded]="'default'"
            (onClick)="onRegulamentClick($event)"
          ></ustore-button>
        </div>

        <div class="form-section-group" *ngIf="!isUtmMode">
          <h2 class="section-title">
            <div class="step-number">1</div>
            {{ 'pages.indication.section1.title' | i18n }}
          </h2>
          <p class="section-description">{{ 'pages.indication.section1.description' | i18n }}</p>
          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="referrerName">{{ 'pages.indication.section1.name.label' | i18n }}</label>
              <umkt-custom-input
                id="referrerName"
                type="text"
                class="input-custom"
                [error]="referrerNameControl?.invalid && referrerNameControl?.touched"
                [placeholder]="'pages.indication.section1.name.placeholder' | i18n"
                formControlName="referrerName"
                [maxlength]="255"
                required
                (keydown)="preventLeadingSpace($event)"
              />
              <span *ngIf="referrerNameControl?.hasError('required') && referrerNameControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div *ngIf="referrerNameControl?.errors?.['noWhitespaceOnly'] && referrerNameControl?.touched" class="error-message">{{ 'pages.indication.section1.name.errorWhitespace' | i18n }}</div>
              <div *ngIf="referrerNameControl?.hasError('maxlength') && referrerNameControl?.touched" class="error-message">{{ 'pages.indication.section1.name.errorMax' | i18n }}</div>
            </div>

            <div class="form-field">
              <label class="field-label" for="referrerPhone">{{ 'pages.indication.section1.phone.label' | i18n }}</label>
              <umkt-custom-input
                id="referrerPhone"
                type="tel"
                class="input-custom"
                [error]="referrerPhoneControl?.invalid && referrerPhoneControl?.touched"
                [placeholder]="'pages.indication.section1.phone.placeholder' | i18n"
                formControlName="referrerPhone"
                [maxlength]="15"
                (input)="applyPhoneMask($event)"
                (keydown)="onPhoneKeydown($event)"
                required
              ></umkt-custom-input>
              <span *ngIf="referrerPhoneControl?.hasError('required') && referrerPhoneControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div *ngIf="referrerPhoneControl?.errors?.['invalidPhone'] && referrerPhoneControl?.touched" class="error-message">{{ 'pages.indication.section1.phone.errorInvalid' | i18n }}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="referrerArea">{{ 'pages.indication.section1.area.label' | i18n }}</label>
              <umkt-custom-input
                id="referrerArea"
                type="text"
                class="input-custom"
                [error]="referrerAreaControl?.invalid && referrerAreaControl?.touched"
                [placeholder]="'pages.indication.section1.area.placeholder' | i18n"
                formControlName="referrerArea"
                [maxlength]="255"
                required
                (keydown)="preventLeadingSpace($event)"
              />
              <span *ngIf="referrerAreaControl?.hasError('required') && referrerAreaControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div *ngIf="referrerAreaControl?.errors?.['noWhitespaceOnly'] && referrerAreaControl?.touched" class="error-message">{{ 'pages.indication.section1.area.errorWhitespace' | i18n }}</div>
              <div *ngIf="referrerAreaControl?.hasError('maxlength') && referrerAreaControl?.touched" class="error-message">{{ 'pages.indication.section1.area.errorMax' | i18n }}</div>
            </div>
            <div class="form-field">
              <label class="field-label" for="referrerEmail">{{ 'pages.indication.section1.email.label' | i18n }}</label>
              <umkt-custom-input
                id="referrerEmail"
                type="email"
                class="input-custom"
                [error]="referrerEmailControl?.invalid && referrerEmailControl?.touched"
                [placeholder]="'pages.indication.section1.email.placeholder' | i18n"
                formControlName="referrerEmail"
                (keydown)="preventLeadingSpace($event)"
                required
              ></umkt-custom-input>
              <span *ngIf="referrerEmailControl?.hasError('required') && referrerEmailControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <span *ngIf="referrerEmailControl?.hasError('corporateEmail') && referrerEmailControl?.touched" class="error-message">{{ 'pages.indication.section1.email.errorInvalid' | i18n }}</span>
              <span *ngIf="referrerEmailControl?.hasError('emailNoSpaces') && referrerEmailControl?.touched" class="error-message">{{ 'pages.indication.section1.email.errorSpaces' | i18n }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="referrerState">{{ 'pages.indication.section1.state.label' | i18n }}</label>
              <select
                id="referrerState"
                class="form-select"
                [class.error]="referrerStateControl?.invalid && referrerStateControl?.touched"
                formControlName="referrerState"
                (change)="onStateChange($event)"
                required
              >
                <option value="">{{ 'pages.indication.section1.state.defaultOption' | i18n }}</option>
                <option *ngFor="let state of brazilStates" [value]="state.value">
                  {{ state.label }}
                </option>
              </select>
              <span *ngIf="referrerStateControl?.hasError('required') && referrerStateControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
            </div>
            <div class="form-field">
              <label class="field-label" for="referrerCity">{{ 'pages.indication.section1.city.label' | i18n }}</label>
              <div class="city-input-container">
                <umkt-custom-input
                  id="referrerCity"
                  type="text"
                  class="input-custom"
                  [class.error]="referrerCityControl?.invalid && referrerCityControl?.touched"
                  [placeholder]="getCityPlaceholder() | i18n"
                  formControlName="referrerCity"
                  (input)="onCityInput($event)"
                  (focus)="onCityFocus()"
                  (keydown)="onCityKeydown($event); preventLeadingSpace($event)"
                  autocomplete="off"
                  required
                ></umkt-custom-input>
              <span *ngIf="referrerCityControl?.hasError('required') && referrerCityControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
                <div *ngIf="showCitySuggestions && filteredCities.length > 0" class="city-suggestions">
                  <div
                    *ngFor="let city of filteredCities; let i = index"
                    class="city-suggestion-item"
                    [class.selected]="i === selectedSuggestionIndex"
                    (click)="selectCity(city)"
                    (mouseenter)="selectedSuggestionIndex = i"
                  >
                    {{ city }}
                  </div>
                </div>
              </div>
              <div
                *ngIf="referrerCityControl?.errors?.['noWhitespaceOnly'] && referrerCityControl?.touched"
                class="error-message"
              >
                {{ 'pages.indication.section1.city.errorWhitespace' | i18n }}
              </div>
              <div
                *ngIf="referrerCityControl?.errors?.['invalidCity'] && referrerCityControl?.touched"
                class="error-message"
              >
                {{ 'pages.indication.section1.city.errorNotFound' | i18n }}
              </div>
            </div>
          </div>
        </div>

        <!-- Company Information Section -->
        <div class="form-section-group">
          <h2 class="section-title company-info-title">
            <div class="step-number">{{ isUtmMode ? 1 : 2 }}</div>
            {{ (isUtmMode ? 'pages.indication.section2Utm.title' : 'pages.indication.section2.title') | i18n }}
          </h2>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="contactName">{{ (isUtmMode ? 'pages.indication.section2Utm.contactName.label' : 'pages.indication.section2.contactName.label') | i18n }}</label>
              <umkt-custom-input
                id="contactName"
                type="text"
                class="input-custom"
                [error]="contactNameControl?.invalid && contactNameControl?.touched"
                [placeholder]="'pages.indication.section2.contactName.placeholder' | i18n"
                formControlName="contactName"
                [maxlength]="255"
                required
                (keydown)="preventLeadingSpace($event)"
              />
              <span *ngIf="contactNameControl?.hasError('required') && contactNameControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div
                *ngIf="contactNameControl?.errors?.['noWhitespaceOnly'] && contactNameControl?.touched"
                class="error-message"
              >
                {{ 'pages.indication.section2.contactName.errorWhitespace' | i18n }}
              </div>
              <div *ngIf="contactNameControl?.hasError('maxlength') && contactNameControl?.touched" class="field-error">
                {{ 'pages.indication.section2.contactName.errorMax' | i18n }}
              </div>
            </div>
            <div class="form-field">
              <label class="field-label" for="companyName">{{ 'pages.indication.section2.companyName.label' | i18n }}</label>
              <umkt-custom-input
                formControlName="companyName"
                id="companyName"
                class="input-custom"
                [ariaLabel]="'pages.indication.section2.companyName.label' | i18n"
                [placeholder]="'pages.indication.section2.companyName.placeholder' | i18n"
                [maxlength]="255"
                class="input-custom"
                [error]="companyNameControl?.invalid && companyNameControl?.touched"
                (keydown)="preventLeadingSpace($event)"
              ></umkt-custom-input>
              <span *ngIf="companyNameControl?.hasError('required') && companyNameControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div *ngIf="companyNameControl?.hasError('maxlength') && companyNameControl?.touched" class="field-error">
                {{ 'pages.indication.section2.companyName.errorMax' | i18n }}
              </div>
              <div
                *ngIf="companyNameControl?.errors?.['noWhitespaceOnly'] && companyNameControl?.touched"
                class="field-error"
              >
                {{ 'pages.indication.section2.companyName.errorWhitespace' | i18n }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="companyCNPJ">{{ 'pages.indication.section2.cnpj.label' | i18n }}</label>
              <umkt-custom-input
                formControlName="companyCNPJ"
                [ariaLabel]="'pages.indication.section2.cnpj.label' | i18n"
                [placeholder]="'pages.indication.section2.cnpj.placeholder' | i18n"
                mask="AA.AAA.AAA/AAAA-00"
                class="input-custom"
                [error]="companyCNPJControl?.invalid && companyCNPJControl?.touched"
              ></umkt-custom-input>
              <span *ngIf="companyCNPJControl?.hasError('required') && companyCNPJControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div
                *ngIf="companyCNPJControl?.errors?.['invalidCNPJ'] && companyCNPJControl?.touched"
                class="field-error"
              >
                {{ 'pages.indication.section2.cnpj.errorInvalid' | i18n }}
              </div>
    
            </div>

            <div class="form-field">
              <label class="field-label" for="companyPhone">{{ 'pages.indication.section2.phone.label' | i18n }}</label>
              <umkt-custom-input
                id="companyPhone"
                type="tel"
                [error]="companyPhoneControl?.invalid && companyPhoneControl?.touched"
                [placeholder]="'pages.indication.section2.phone.placeholder' | i18n"
                formControlName="companyPhone"
                class="input-custom"
                [maxlength]="15"
                (input)="applyPhoneMask($event)"
                (keydown)="onPhoneKeydown($event)"
                required
              ></umkt-custom-input>
              <span *ngIf="companyPhoneControl?.hasError('required') && companyPhoneControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div
                *ngIf="companyPhoneControl?.errors?.['invalidPhone'] && companyPhoneControl?.touched"
                class="error-message"
              >
                {{ 'pages.indication.section2.phone.errorInvalid' | i18n }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="companyEmail">{{ 'pages.indication.section2.email.label' | i18n }}</label>
              <umkt-custom-input
                id="companyEmail"
                class="input-custom"
                type="email"
                [error]="companyEmailControl?.invalid && companyEmailControl?.touched"
                [placeholder]="'pages.indication.section2.email.placeholder' | i18n"
                formControlName="companyEmail"
                (keydown)="preventLeadingSpace($event)"
                required
              ></umkt-custom-input>
              <span *ngIf="companyEmailControl?.hasError('required') && companyEmailControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
              <div
                *ngIf="companyEmailControl?.errors?.['email'] && companyEmailControl?.touched"
                class="error-message"
              >
                {{ 'pages.indication.section2.email.errorInvalid' | i18n }}
              </div>
              <div
                *ngIf="companyEmailControl?.errors?.['emailWithSpaces'] && companyEmailControl?.touched"
                class="error-message"
              >
                {{ 'pages.indication.section2.email.errorSpaces' | i18n }}
              </div>
            </div>
            <div class="form-field">
              <label class="field-label" for="bestContactMethod">{{ 'pages.indication.section2.contactMethod.label' | i18n }}</label>
              <select
                id="bestContactMethod"
                class="form-select"
                [class.error]="bestContactMethodControl?.invalid && bestContactMethodControl?.touched"
                formControlName="bestContactMethod"
                required
              >
                <option value="">{{ 'pages.indication.section2.contactMethod.defaultOption' | i18n }}</option>
                <option value="telefone">{{ 'pages.indication.section2.contactMethod.options.phone' | i18n }}</option>
                <option value="whatsapp">{{ 'pages.indication.section2.contactMethod.options.whatsapp' | i18n }}</option>
                <option value="email">{{ 'pages.indication.section2.contactMethod.options.email' | i18n }}</option>
              </select>
              <span *ngIf="bestContactMethodControl?.hasError('required') && bestContactMethodControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label class="field-label" for="bestContactTime">{{ 'pages.indication.section2.contactTime.label' | i18n }}</label>
              <umkt-custom-input
                id="bestContactTime"
                type="text"
                class="input-custom"
                [error]="bestContactTimeControl?.invalid && bestContactTimeControl?.touched"
                [placeholder]="'pages.indication.section2.contactTime.placeholder' | i18n"
                formControlName="bestContactTime"
                [maxlength]="100"
                required
                (keydown)="preventLeadingSpace($event)"
              ></umkt-custom-input>
              <span *ngIf="bestContactTimeControl?.hasError('required') && bestContactTimeControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
            </div>
            <div class="form-field">
              <label class="field-label" for="companyAwareOfReferral">{{ 'pages.indication.section2.aware.label' | i18n }}</label>
              <select
                id="companyAwareOfReferral"
                class="form-select"
                [class.error]="companyAwareOfReferralControl?.invalid && companyAwareOfReferralControl?.touched"
                formControlName="companyAwareOfReferral"
                required
              >
                <option value="">{{ 'pages.indication.section2.aware.defaultOption' | i18n }}</option>
                <option value="sim">{{ 'pages.indication.section2.aware.yes' | i18n }}</option>
                <option value="nao">{{ 'pages.indication.section2.aware.no' | i18n }}</option>
              </select>
              <span *ngIf="companyAwareOfReferralControl?.hasError('required') && companyAwareOfReferralControl?.touched" class="error-message">{{ 'shared.messages.required' | i18n }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-field full-width">
              <label class="field-label" for="additionalInformation">{{ 'pages.indication.section2.additionalInfo.label' | i18n }}</label>
              <umkt-custom-input
                id="additionalInformation"
                type="text"
                class="input-custom"
                formControlName="additionalInformation"
                [placeholder]="'pages.indication.section2.additionalInfo.placeholder' | i18n"
                [maxlength]="500"
                (keydown)="preventLeadingSpace($event)"
              ></umkt-custom-input>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions right-align">
          <button 
            type="submit" 
            class="submit-btn"
            [disabled]="isSubmitting || indicationForm.invalid"
            [ngClass]="{'disabled-btn': isSubmitting || indicationForm.invalid}"
          >
            <div *ngIf="isSubmitting">{{ 'pages.indication.submittingButton' | i18n }}</div>
            <div *ngIf="!isSubmitting">{{ 'pages.indication.submitButton' | i18n }}</div>
          </button>
        </div>
      </form>
    </div>
  </div>
</div>